import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, AlertCircle, CheckCircle, Clock, Wallet, Eye, Upload, Phone, ChevronRight, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useAuth } from '@/hooks/useAuth';
import apiService from '@/services/api';

interface Order {
  id: number;
  reference: string;
  service_name: string;
  location: string;
  estimated_cost: number;
  status: string;
  progress: number;
}

interface Quote {
  id: number;
  service_name: string;
  amount: number;
  description: string;
  status: string;
  approval_status?: string;  // 'pending', 'provider_assigned', 'customer_approved', etc.
}

interface Property {
  id: number;
  name: string;
  unit: string;
}

interface ServiceRequest {
  id: string;
  service_category: string;
  service_category_name: string;
  property_type: string;
  service_type: string;
  service_type_name: string;
  contact_email: string;
  contact_name: string;
  status: string;
  created_at: string;
  quoted_price?: number;
}

interface DashboardStats {
  activeOrders: number;
  awaitingApproval: number;
  awaitingPayment: number;
  completedThisMonth: number;
}

const CustomerDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    activeOrders: 0,
    awaitingApproval: 0,
    awaitingPayment: 0,
    completedThisMonth: 0,
  });
  const [orders, setOrders] = useState<Order[]>([]);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [serviceRequests, setServiceRequests] = useState<ServiceRequest[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [walletBalance, setWalletBalance] = useState(0);

  // Fetch all dashboard data.
  // Run each fetch in parallel + isolated so one transient failure does not
  // wipe out the rest of the page (previous bug: a single rejected request
  // skipped every subsequent fetch and rendered an empty dashboard).
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      const safeGet = async (url: string) => {
        try { return await apiService.request(url, 'GET'); }
        catch { return { success: false } as any; }
      };
      try {
        const [statsRes, ordersRes, quotesRes, requestsRes, propsRes, walletRes] = await Promise.all([
          safeGet('/customer/dashboard/stats'),
          safeGet('/customer/orders'),
          safeGet('/customer/quotes'),
          safeGet('/customer/service-requests'),
          safeGet('/customer/properties'),
          safeGet('/customer/wallet'),
        ]);

        if (statsRes?.success && statsRes.data) {
          const d = statsRes.data;
          setStats({
            activeOrders: d.activeOrders ?? d.upcoming_bookings ?? d.active_orders ?? 0,
            awaitingApproval: d.awaitingApproval ?? d.awaiting_approval ?? d.pending_quotes ?? 0,
            awaitingPayment: d.awaitingPayment ?? d.awaiting_payment ?? d.pending_invoices ?? 0,
            completedThisMonth: d.completedThisMonth ?? d.completed_bookings ?? d.completed_this_month ?? 0,
          });
        }
        if (ordersRes?.success && Array.isArray(ordersRes.data))   setOrders(ordersRes.data);
        if (quotesRes?.success && Array.isArray(quotesRes.data))   setQuotes(quotesRes.data);
        if (requestsRes?.success && Array.isArray(requestsRes.data)) setServiceRequests(requestsRes.data);
        if (propsRes?.success && Array.isArray(propsRes.data))     setProperties(propsRes.data);
        if (walletRes?.success && walletRes.data) {
          const balance = walletRes.data.balance || walletRes.data.availableBalance || 0;
          setWalletBalance(Math.max(0, Number(balance)));
        }
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      fetchDashboardData();
    }
  }, [user?.id]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-accent" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 lg:p-8 space-y-6 md:space-y-8">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-accent to-accent/80 text-white rounded-2xl p-6 md:p-8 lg:p-12 shadow-lg">
          <div className="max-w-2xl">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2">Welcome to Virewire, {user?.first_name || 'Guest'}</h1>
            <p className="text-sm md:text-base text-accent-foreground/90 mb-6">
              We are here to elevate your property management experience. Start by
              adding your first property or requesting a concierge service.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
              <Link to="/register-property" className="w-full sm:w-auto">
                <Button className="w-full sm:w-auto bg-white text-accent hover:bg-gray-50">
                  + Add Property
                </Button>
              </Link>
              <Link to="/dashboard/request-service" className="w-full sm:w-auto">
                <Button variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-white/10">
                  Request a Service
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
          <StatCard icon="🛒" label="ACTIVE" value={stats.activeOrders.toString()} sublabel="Active Orders" />
          <StatCard icon="⏳" label="REVIEW" value={stats.awaitingApproval.toString()} sublabel="Awaiting Approval" />
          <StatCard icon="💳" label="PAYMENT" value={stats.awaitingPayment.toString()} sublabel="Awaiting Payment" />
          <StatCard icon="✅" label="MONTHLY" value={stats.completedThisMonth.toString()} sublabel="Completed This Month" />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {/* Left Column - 2/3 width */}
          <div className="col-span-1 md:col-span-2 space-y-6 md:space-y-8">
            {/* Action Required */}
            <Card>
              <CardContent className="p-4 md:p-6">
                <div className="flex items-center gap-3 mb-4 md:mb-6">
                  <AlertCircle className="text-red-500 flex-shrink-0" size={20} />
                  <h2 className="text-lg md:text-xl font-bold">Action Required</h2>
                </div>

                {quotes && quotes.some(q => q.status !== 'customer_approved') ? (
                  <div className="space-y-2 md:space-y-3">
                    {/* Pending Quotes - need approval */}
                    {quotes.filter(q => q.status !== 'customer_approved').slice(0, 3).map((quote) => (
                      <div key={`quote-${quote.id}`} className="flex flex-col sm:flex-row sm:items-start sm:justify-between p-3 md:p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <div className="flex items-start gap-3 flex-1 mb-3 sm:mb-0">
                          <div className="w-8 h-8 md:w-10 md:h-10 bg-yellow-100 rounded flex items-center justify-center flex-shrink-0 text-sm md:text-base">
                            📋
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-sm md:text-base">Quote #{quote.id}</h4>
                            <p className="text-xs md:text-sm text-gray-600 truncate md:truncate">{quote.service_name}</p>
                          </div>
                        </div>
                        <Link to={`/dashboard/quotes/${quote.id}`} className="flex-shrink-0">
                          <Button variant="link" className="text-accent p-0 h-auto text-sm md:text-base justify-start sm:justify-end">Review Quote</Button>
                        </Link>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-gray-500 text-sm">No action required</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Active Orders */}
            <Card>
              <CardContent className="p-4 md:p-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 md:mb-6 gap-3">
                  <h2 className="text-lg md:text-xl font-bold">Active Orders</h2>
                  <Link to="/dashboard/bookings" className="text-accent text-xs md:text-sm font-semibold">View All</Link>
                </div>

                {orders && orders.length > 0 ? (
                  <div className="grid grid-cols-1 gap-4 md:gap-6">
                    {orders.slice(0, 2).map((order) => (
                      <div key={order.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition">
                        <div className="flex flex-col sm:flex-row gap-4 p-4">
                          {/* Image Placeholder */}
                          <div className="flex-shrink-0 w-full sm:w-24 md:w-32 bg-gradient-to-br from-accent/20 to-accent/10 rounded-lg flex items-center justify-center h-24 sm:h-32">
                            <span className="text-3xl">🔧</span>
                          </div>

                          {/* Content */}
                          <div className="flex-1 flex flex-col justify-between min-w-0">
                            {/* Top Section */}
                            <div>
                              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
                                <div className="min-w-0 flex-1">
                                  <p className="text-xs font-semibold text-gray-600 mb-1">ORD-{order.id}</p>
                                  <h4 className="font-bold text-sm md:text-base text-gray-900 line-clamp-2">{order.service_name || 'Service'}</h4>
                                  <p className="text-xs text-gray-600 mt-1">{order.property_name || 'Property'}</p>
                                </div>
                                <div className="flex-shrink-0">
                                  <p className="text-xs uppercase font-bold text-gray-600">Amount</p>
                                  <p className="text-lg md:text-xl font-bold text-green-600">£{((order.total_amount_pence || 0) / 100).toFixed(2)}</p>
                                </div>
                              </div>

                              {/* Status Tags */}
                              <div className="flex flex-wrap gap-2 mb-3">
                                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                                  order.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                  order.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                  order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {order.status?.toUpperCase() || 'PENDING'}
                                </span>
                              </div>
                            </div>

                            {/* Buttons */}
                            <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-gray-100">
                              <Link to={`/order/${order.id}`} className="flex-1 sm:flex-none">
                                <Button variant="outline" size="sm" className="text-xs md:text-sm w-full">View Details</Button>
                              </Link>
                              <Link to={`/order/${order.id}`}>
                                <Button size="sm" className="bg-accent hover:bg-accent/90 text-xs md:text-sm flex-1 sm:flex-none">Track Progress</Button>
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500 text-sm">No active orders</p>
                    <Link to="/dashboard/request-service" className="text-accent text-sm font-semibold mt-2 inline-block">Request a service</Link>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Service Requests */}
            <Card>
              <CardContent className="p-4 md:p-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 md:mb-6 gap-3">
                  <h2 className="text-lg md:text-xl font-bold">Service Requests</h2>
                  <Link to="/dashboard/service-requests" className="text-accent text-xs md:text-sm font-semibold">View All Services</Link>
                </div>

                {serviceRequests && serviceRequests.filter(req => req.status !== 'paid').length > 0 ? (
                  <div className="space-y-3 md:space-y-4">
                    {serviceRequests.filter(req => req.status !== 'paid').slice(0, 5).map((req: ServiceRequest) => (
                      <div key={req.id} className="border border-gray-200 rounded-lg p-3 md:p-4 hover:shadow-md transition">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-2 md:mb-3">
                          <div className="flex-1">
                            <h4 className="font-semibold text-sm md:text-base">
                              {req.service_type_name?.replace('-', ' ') || 'Service Request'}
                            </h4>
                            <p className="text-xs md:text-sm text-gray-600">
                              {req.service_category_name} {req.property_type && `• ${req.property_type}`}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 whitespace-nowrap">
                            <span className={`px-2 md:px-3 py-1 text-xs md:text-sm rounded-full font-medium ${
                              req.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              req.status === 'quoted' ? 'bg-blue-100 text-blue-800' :
                              req.status === 'accepted' ? 'bg-purple-100 text-purple-800' :
                              req.status === 'paid' ? 'bg-emerald-100 text-emerald-800' :
                              req.status === 'completed' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {req.status?.toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs md:text-sm text-gray-500 mb-3">
                          {req.contact_name && <span>{req.contact_name} • </span>}
                          {req.contact_email}
                          {req.quoted_price && ` • Quote: £${parseFloat(req.quoted_price).toFixed(2)}`}
                        </p>
                        <div className="flex gap-2">
                          <Link to={`/dashboard/service-requests/${req.id}`} className="flex-1">
                            <Button variant="outline" size="sm" className="text-xs md:text-sm w-full">View Details</Button>
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500 text-sm">No service requests yet</p>
                    <Link to="/dashboard/request-service" className="text-accent text-sm font-semibold mt-2 inline-block">Request a service</Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - 1/3 width */}
          <div className="col-span-1 space-y-4 md:space-y-6">

            {/* Your Properties */}
            <Card>
              <CardContent className="p-4 md:p-6">
                <h3 className="font-bold text-gray-900 mb-3 md:mb-4 text-sm md:text-base">Your Properties</h3>
                <div className="space-y-2 md:space-y-3">
                  {properties && properties.length > 0 ? (
                    properties.slice(0, 2).map((property: any) => (
                      <div key={property.id} className="p-2 md:p-3 border border-gray-200 rounded-lg hover:border-accent hover:bg-accent/5 cursor-pointer">
                        <p className="font-semibold text-xs md:text-sm">{property.name || 'Property'}</p>
                        <p className="text-xs text-gray-600">{property.unit || property.address || 'Address'}</p>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-xs text-gray-600">No properties added yet</p>
                    </div>
                  )}
                </div>
                <Link to="/dashboard/properties">
                  <Button variant="link" className="w-full mt-3 md:mt-4 text-accent text-xs md:text-sm">
                    MANAGE {properties?.length || 0} PROPERTIES
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card>
              <CardContent className="p-4 md:p-6">
                <h3 className="font-bold text-gray-900 mb-3 md:mb-4 text-sm md:text-base">Recent Activity</h3>
                <div className="space-y-3 md:space-y-4">
                  {orders && orders.length > 0 ? (
                    orders.slice(0, 3).map((order: any, i: number) => (
                      <div key={i} className="pb-3 md:pb-4 border-b last:border-b-0">
                        <div className="flex gap-2 md:gap-3">
                          <div className="w-2 h-2 rounded-full bg-accent flex-shrink-0 mt-1 md:mt-1.5"></div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-accent font-semibold">{order.created_at ? new Date(order.created_at).toLocaleDateString() : 'Recent'}</p>
                            <p className="text-xs md:text-sm font-semibold text-gray-900">ORD-{order.id}: {order.service_name || 'Service'}</p>
                            <p className="text-xs text-gray-600">{order.status?.charAt(0).toUpperCase() + order.status?.slice(1) || 'Status pending'}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-xs text-gray-600">No activity yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Quick Wallet */}
            <Card className="bg-gradient-to-br from-accent to-accent/80 text-white">
              <CardContent className="p-4 md:p-6">
                <div className="mb-3 md:mb-4 flex items-center gap-2">
                  <Wallet size={18} />
                  <h3 className="font-bold uppercase tracking-wide text-xs md:text-sm">Quick Wallet</h3>
                </div>
                <p className="text-xs text-accent-foreground/80 mb-2 md:mb-3">Available Credits</p>
                <p className="text-2xl md:text-4xl font-bold mb-4 md:mb-6">£{parseFloat(walletBalance || 0).toFixed(2)}</p>
                <Link to="/wallet/add-funds">
                  <Button className="w-full bg-white text-accent hover:bg-gray-50 text-xs md:text-sm">Top Up Balance</Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

function StatCard({ icon, label, value, sublabel }: { icon: string; label: string; value: string; sublabel: string }) {
  return (
    <div className="bg-white rounded-lg p-3 md:p-6 border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between mb-2 md:mb-4">
        <span className="text-2xl md:text-3xl">{icon}</span>
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">{label}</p>
      </div>
      <p className="text-2xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2">{value}</p>
      <p className="text-xs md:text-sm text-gray-600">{sublabel}</p>
    </div>
  );
}

export default CustomerDashboard;
